import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as amplitude from "@amplitude/analytics-browser";

import { PageStatus } from "@unified-checkout/design-system";
import { CircleExclamation } from "@unified-checkout/design-system/icons/index";

import { SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import {
  BULK_PAYMENT_LINKS_EVENTS,
  BULK_PAYMENT_LINKS_EVENTS_PARAMS,
  BULK_PAYMENT_LINKS_EVENTS_VALUES
} from "@app/dashboard/constants/tracking_events/payment_links/bulk";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { getCurrentMode } from "@app/dashboard/utils/getCurrentMode";

import { CustomersSheet } from "./sections/CustomersSheet";
import { PaymentSection } from "./sections/Payment";
import { Restrictions } from "./sections/Restrictions";

import {
  AMOUNTS_FROM,
  type TBulkSubmissionData,
  type TUploadedFromValue
} from "../utils";
import { Footer } from "./sections/Footer";
import styles from "./bulk-payment-links.module.scss";


export const BulkPaymentLinks = () => {
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();
  const [fullPageStatus, setFullPageStatus] = useState({
    loading: true,
    error: false,
    disabled: false
  });
  const [fixedAmount, setFixedAmount] = useState<string>();
  const [amountsFrom, setAmountsFrom] = useState<TUploadedFromValue>(AMOUNTS_FROM.UPLOADED);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formValues, setFormValues] = useState<TBulkSubmissionData | {}>({});

  const isFirstRender = useRef(true);

  useEffect(() => {
    amplitude.track(BULK_PAYMENT_LINKS_EVENTS.PAGE_VIEWED, {
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive)
    });
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    };

    amplitude.track(BULK_PAYMENT_LINKS_EVENTS.AMOUNT_SOURCE_SELECTED, {
      [BULK_PAYMENT_LINKS_EVENTS_PARAMS.AMOUNT_SOURCE]: amountsFrom === 'fixed' ?
        BULK_PAYMENT_LINKS_EVENTS_VALUES.FIXED
        :
        BULK_PAYMENT_LINKS_EVENTS_VALUES.UPLOADED_SHEET,
    });
  }, [amountsFrom]);


  return (
    <div
      className={styles["bulk-payment-links"]}
      data-bulk-pl-creation-disabled={fullPageStatus.disabled}
    >
      {
        fullPageStatus.error ?
          <PageStatus
            status="error"
            title={t('errors.somethingWentWrong')}
            icon={CircleExclamation}
          />
          :
          <>
            <CustomersSheet
              amountsFrom={amountsFrom}
              fixedAmount={fixedAmount}
              setFormValues={setFormValues}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              fullPageStatus={fullPageStatus}
            />
            <PaymentSection
              amountsFrom={amountsFrom}
              setAmountsFrom={setAmountsFrom}
              fixedAmount={fixedAmount}
              setFixedAmount={setFixedAmount}
              setFormValues={setFormValues}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              fullPageStatus={fullPageStatus}
              setFullPageStatus={setFullPageStatus}
            />
            <Restrictions
              setFormValues={setFormValues}
              fullPageStatus={fullPageStatus}
            />
            <Footer
              formValues={formValues}
              setFormValues={setFormValues}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              fullPageStatus={fullPageStatus}
              setFullPageStatus={setFullPageStatus}
              amountsFrom={amountsFrom}
            />
          </>
      }
    </div >
  )
}
